import React, { FC, useMemo } from "react";
import clsx from "clsx";
import { moneyWithComma } from "utils/form/helpers";
import { Plan } from "services/quotes/types";
import { getGreenEnergyImage, getLogoBySupplier } from "config/suppliers";
import RatesLoader from "containers/retail/sections/energy-rates-3/RatesLoader";
import { HumanViewUnit } from "utils/data/types";
import RatesSortableCell from "containers/retail/sections/energy-rates-3/RatesSortableCell";
import { Theme } from "evcharging/app/themes";
import { css } from "@emotion/css";
import useTheme from "evcharging/hooks/useTheme";
import RatesSelectPlanButton from "containers/retail/sections/energy-rates-3/RatesSelectPlanButton";
import RatesMoreDetailsTable from "containers/retail/sections/energy-rates-3/RatesMoreDetailsTable";
import Desktop from "evcharging/components/Desktop";
import useI18n from "evcharging/hooks/useI18n";

interface PlansTableProps {
  data: unknown[];

  onTouchPlan(plan: Plan): void;

  onSelect: (plan: Plan) => void;
  sort: string;
  isLoading: boolean;

  onSort(sort: string): void;
}

const createColumns = (unit: string, t) => [
  {
    name: t("rates.supplier"),
    sortable: true,
    property: "supplier"
  },
  {
    name: t("rates.term"),
    sortable: true,
    property: "term"
  },
  {
    name: t("rates.price-per-unit", { unit }),
    sortable: true,
    property: "price"
  }
];

const RatesAllPlansTable: FC<PlansTableProps> = (props) => {
  const { data, onSelect, onTouchPlan, sort, onSort, isLoading } = props;

  const { t } = useI18n();

  const columns = useMemo(() => createColumns(HumanViewUnit.KWH, t), []);
  const theme = useTheme();
  const classes = createStyles(theme);
  const onTouch = (plan) => () => onTouchPlan(plan);

  return (
    <div className={classes.tableContainer}>
      {isLoading && (
        <div className={classes.loaderWrapper}>
          <RatesLoader />
        </div>
      )}
      <table className={classes.root}>
        <thead>
        <tr>
          {columns.map((column) => (
            <RatesSortableCell key={column.name} column={column} sort={sort} onSort={onSort} />
          ))}
          <th />
        </tr>
        </thead>
        <tbody className={clsx(isLoading ? classes.tableOverflow : false)}>
        {data.map((plan: Plan) => (
          <tr key={plan.planId}>
            <td className={clsx(classes.cell, classes.firstCell)}>
              <div className={clsx(classes.innerCell, classes.supplierName)} onClick={onTouch(plan)}>
                <img
                  className={classes.logoCompany}
                  alt={plan.supplier.name}
                  title={plan.supplier.name}
                  src={getLogoBySupplier(plan.supplier)}
                />
              </div>
            </td>
            <td className={classes.cell}>
              <div className={clsx(classes.innerCell, classes.month)} onClick={onTouch(plan)}>
                <div className={classes.pillDefault}>
                  {t("rates.n-months", { n: plan.term.value })}
                </div>
              </div>
            </td>
            <td className={classes.cell}>
              <div className={clsx(classes.innerCell, classes.price)} onClick={onTouch(plan)}>
                <div className={classes.pillPrimary}>
                  {`¢${moneyWithComma(plan.price.amount * 100, 3)}`}
                </div>
                {
                  getGreenEnergyImage(plan) && (
                    <img
                      alt={"Green energy"}
                      title={"Green energy"}
                      src={getGreenEnergyImage(plan)}
                    />
                  )
                }
              </div>
            </td>
            <td className={classes.cell}>
              <Desktop>
                <div className={clsx(classes.lastCell, classes.greenEnergy)}>
                  <div className={classes.rowButtons}>
                    <RatesSelectPlanButton plan={plan} onSelect={onSelect} />
                    <RatesMoreDetailsTable plan={plan} />
                  </div>
                </div>
              </Desktop>
            </td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );
};

const createStyles = (theme: Theme) => ({
  root: css({
    maxWidth: "100%",
    width: "100%",
    borderSpacing: 0,
  }),
  cell: css({
    padding: 0,
    border: "none"
  }),
  firstCell: css({
    borderRadius: theme.spacing(0.75, 0, 0, 0.75)
  }),
  lastCell: css({
    borderRadius: theme.spacing(0, 0.75, 0.75, 0),
    backgroundColor: "#fff",
    margin: theme.spacing(0, 0, 1.875),
    padding: theme.spacing(1.25, 2.6),
    height: 75,
    alignItems: "center",
    display: "flex",
    fontWeight: 600
  }),
  innerCell: css({
    backgroundColor: "#fff",
    margin: theme.spacing(0, 0, 1.875),
    padding: theme.spacing(1.25, 1.2),
    height: 75,
    alignItems: "center",
    display: "flex",
    fontWeight: 600,
    [theme.breakpoints.mobile.up]: {
      pointerEvents: "none",
      padding: theme.spacing(1.25, 2.6),
    }
  }),
  supplierName: css({
    maxWidth: 260,
    fontWeight: 700,
    paddingRight: 0,
    borderRadius: theme.spacing(0.75, 0, 0, 0.75)
  }),
  logoCompany: css({
    maxWidth: 100,
    display: "block",
    [theme.breakpoints.mobile.up]: {
      maxWidth: 150
    }
  }),
  month: css({
    [theme.breakpoints.mobile.down]: {
      minWidth: 132
    }
  }),
  price: css({
    img: {
      marginLeft: 100,
      width: 32,
      [theme.breakpoints.mobile.down]: {
        width: 24,
        marginLeft: 12,
      }
    }
  }),
  greenEnergy: css({
    minWidth: "30vw",
    display: "flex",
    justifyContent: "space-between",
  }),
  tableContainer: css({
    position: "relative"
  }),
  loaderWrapper: css({
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    zIndex: 1
  }),
  tableOverflow: css({
    opacity: 0.7
  }),
  pillDefault: css({
    padding: 6,
    color: "#000",
    backgroundColor: "rgb(247, 248, 250)",
    borderRadius: 4,
    [theme.breakpoints.mobile.down]: {
      padding: 4
    }
  }),
  pillPrimary: css({
    padding: 6,
    color: "#2e74b9",
    backgroundColor: "#e9f1f8",
    borderRadius: 4,
    [theme.breakpoints.mobile.down]: {
      padding: 4
    }
  }),
  rowButtons: css({
    display: "flex",
    marginLeft: "auto",
    "> * + *": {
      marginLeft: 12
    }
  })
});

export default RatesAllPlansTable;
